import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Cpu, Key, KeyRound, CheckCircle2, AlertCircle, Loader2, Save,
  Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type ProviderId = 'anthropic' | 'bedrock' | 'vertex';

interface AllowedModel {
  id: string;
  name: string;
  pricePerMInput: number;
  pricePerMOutput: number;
}

interface OrgProviderRow {
  provider: ProviderId;
  name: string;
  description: string;
  keyLabel: string;
  keyHint: string;
  enabledByPlatform: boolean;
  platformKeyConfigured: boolean;
  allowedModels: AllowedModel[];
  platformDefaultModel: string | null;
  useOwnKey: boolean;
  orgKeyConfigured: boolean;
  selectedModel: string | null;
  effectiveModel: string | null;
}

export function ProvidersTab({ orgId }: { orgId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['org', orgId, 'providers'],
    queryFn: () => apiClient.listOrgProviders(orgId),
  });
  const providers = (data?.data as OrgProviderRow[] | undefined) ?? [];

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="flex items-start gap-3">
          <Cpu className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">AI providers</p>
            <p className="text-xs text-muted-foreground">
              Pick which model your org's agents use. Leave the BYOK toggle off to use platform-paid keys, or paste your own key to be billed directly by the provider.
            </p>
          </div>
        </div>
      </Card>

      {isLoading && (
        <Card className="p-8 text-center text-muted-foreground">Loading providers…</Card>
      )}

      {!isLoading && providers.filter((p) => p.enabledByPlatform).length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">
          No providers are enabled by the platform yet. Ask a platform administrator to enable at least one provider.
        </Card>
      )}

      {providers.filter((p) => p.enabledByPlatform).map((p) => (
        <ProviderRowCard key={p.provider} orgId={orgId} row={p} />
      ))}

      {providers.some((p) => !p.enabledByPlatform) && (
        <Card className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Disabled by platform</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            {providers.filter((p) => !p.enabledByPlatform).map((p) => (
              <li key={p.provider}>· {p.name}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ProviderRowCard({ orgId, row }: { orgId: string; row: OrgProviderRow }) {
  const qc = useQueryClient();
  const [useOwnKey, setUseOwnKey] = useState(row.useOwnKey);
  // Drop a stale selectedModel that's no longer in the platform's
  // allowed list (catalog refresh, or platform admin removed it).
  // Falls back to "use platform default" rather than carrying the dead
  // ID into a save.
  const allowedIds = new Set(row.allowedModels.map((m) => m.id));
  const cleanSelected = row.selectedModel && allowedIds.has(row.selectedModel) ? row.selectedModel : null;
  const [selectedModel, setSelectedModel] = useState<string | null>(cleanSelected);
  const [keyValue, setKeyValue] = useState('');
  const [showKey, setShowKey] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['org', orgId, 'providers'] });

  const update = useMutation({
    mutationFn: (input: { useOwnKey?: boolean; selectedModel?: string | null; key?: string | null }) =>
      apiClient.updateOrgProvider(orgId, row.provider, input),
    onSuccess: () => { refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });

  function saveSettings() {
    update.mutate(
      { useOwnKey, selectedModel },
      { onSuccess: () => { toast.success(`${row.name} settings saved`); refresh(); } },
    );
  }

  function saveKey() {
    update.mutate(
      { key: keyValue, useOwnKey: true },
      {
        onSuccess: () => {
          toast.success(`${row.name} key saved`);
          setKeyValue('');
          setUseOwnKey(true);
          refresh();
        },
      },
    );
  }

  function clearKey() {
    if (!confirm(`Clear your ${row.name} key? This org will fall back to the platform default key.`)) return;
    update.mutate(
      { key: '', useOwnKey: false },
      {
        onSuccess: () => {
          toast.success(`${row.name} key cleared`);
          setUseOwnKey(false);
          refresh();
        },
      },
    );
  }

  const dirty = useOwnKey !== row.useOwnKey || selectedModel !== row.selectedModel;

  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-bold text-foreground">{row.name}</h3>
          <p className="text-xs text-muted-foreground">{row.description}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {row.orgKeyConfigured ? (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-india-green/15 text-india-green inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> your key
            </span>
          ) : row.platformKeyConfigured ? (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> using platform key
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-600 dark:text-yellow-500 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> no key available
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-4 space-y-5">
        {/* Model picker */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Model</p>
          {row.allowedModels.length === 0 ? (
            <p className="text-xs text-muted-foreground">No models allowed by the platform yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {row.allowedModels.map((m) => {
                const checked = selectedModel === m.id;
                const isPlatformDefault = row.platformDefaultModel === m.id;
                return (
                  <label key={m.id}
                    className={cn(
                      'flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                      checked ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/40',
                    )}
                  >
                    <input
                      type="radio"
                      name={`model-${row.provider}`}
                      checked={checked}
                      onChange={() => setSelectedModel(m.id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        {m.name}
                        {isPlatformDefault && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-saffron/15 text-saffron">
                            platform default
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground font-mono truncate">{m.id}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">${m.pricePerMInput}/M in · ${m.pricePerMOutput}/M out</p>
                    </div>
                  </label>
                );
              })}
              <button
                type="button"
                onClick={() => setSelectedModel(null)}
                className={cn(
                  'flex items-start gap-2 px-3 py-2 rounded-lg border text-left transition-colors',
                  selectedModel === null ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/40',
                )}
              >
                <input type="radio" name={`model-${row.provider}`} checked={selectedModel === null} onChange={() => {}} className="mt-1" />
                <div>
                  <p className="text-sm font-medium">Use platform default</p>
                  <p className="text-[11px] text-muted-foreground">{row.platformDefaultModel ?? '— not set —'}</p>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* BYOK toggle */}
        <div className="flex items-center justify-between gap-4 flex-wrap pt-3 border-t border-border">
          <div>
            <p className="text-sm font-medium">Use my own API key</p>
            <p className="text-xs text-muted-foreground">When off, runs are billed against the platform default. When on, your key is used and you pay the provider directly.</p>
          </div>
          <label className="text-sm font-medium inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useOwnKey}
              onChange={(e) => setUseOwnKey(e.target.checked)}
              className="rounded border-border"
              disabled={!row.orgKeyConfigured && !keyValue.trim()}
              title={!row.orgKeyConfigured && !keyValue.trim() ? 'Save a key below first' : undefined}
            />
            BYOK
          </label>
        </div>

        <div>
          <Button onClick={saveSettings} disabled={!dirty || update.isPending}>
            {update.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save settings
          </Button>
        </div>
      </div>

      {/* Key management */}
      <div className="px-5 py-4 border-t border-border bg-muted/20">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">{row.keyLabel}</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Stored in AWS Secrets Manager. Trinetra never logs or echoes your key back. {row.keyHint}
        </p>
        <div className="flex items-stretch gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] relative">
            <Key className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type={showKey ? 'text' : 'password'}
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              placeholder={row.orgKeyConfigured ? '••••••• (key already set — paste to replace)' : row.keyHint}
              className="pl-8 pr-9"
            />
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button onClick={saveKey} disabled={!keyValue.trim() || update.isPending}>
            {row.orgKeyConfigured ? 'Replace key' : 'Save key'}
          </Button>
          {row.orgKeyConfigured && (
            <Button variant="outline" onClick={clearKey} disabled={update.isPending}>Clear</Button>
          )}
        </div>
      </div>
    </Card>
  );
}
